import React from 'react';
import PropTypes from 'prop-types';

import AvailableLanguageLink from '../AvailableLanguageLink';
import { Link } from 'src/components/Inputs';

/*
 * When making changes to this file be sure that any changes
 * also are made to the corresponding iOS specific counterpart
 * in src/pages/<lang>/legal/ios.jsx.
 */

const propTypes = {
  id: PropTypes.string,
  availableLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};

const FrVersion = ({ id, availableLanguages }) => {
  return (
    <>
      <h4 id={id || 'politique-relative-aux-cookies'}>
        Politique relative aux cookies
      </h4>

      <p>Available languages:</p>
      <ul>
        {availableLanguages.map((language, i) => {
          return (
            <AvailableLanguageLink
              key={`language-${i}`}
              href={language.href}
              label={language.label}
            />
          );
        })}
      </ul>
      <br />
      <br />

      <p>Date d’entrée en vigueur : 22 août 2023</p>
      <p>
        La présente politique relative aux cookies (« Politique ») explique
        comment Plaid Inc. et ses filiales, y compris Plaid Financial Ltd.,
        Plaid Consumer Reporting Agency, Inc., et Plaid, B.V., (collectivement,
        « nous », « notre » ou « nos ») et des tiers de confiance, utilisent des
        cookies sur nos sites Web et autres services en ligne (collectivement,
        les « Services »), ainsi que vos choix liés à ces cookies. Veuillez
        consulter la présente Politique pour en apprendre plus sur les types de
        cookies que nous utilisons, pourquoi nous les utilisons et comment vous
        pouvez contrôler ou limiter l’utilisation des cookies.
      </p>
      <p>
        Pour en savoir plus sur nos pratiques en matière de confidentialité,
        veuillez consulter notre{' '}
        <Link href='#política-de-privacidad-aplicable-al-usuario-final'>
          <a>Politique de confidentialité de l’utilisateur final </a>
        </Link>{' '}
        ainsi que notre{' '}
        <Link href='#déclaration-de-confidentialité'>
          <a>Déclaration de confidentialité</a>
        </Link>
        .
      </p>

      <p>
        <em>Aller à la section :</em>
      </p>
      <p>
        <Link href='#que-sont-les-cookies'>
          <a data-smooth-scroll data-offset='80'>
            Que sont les cookies ?
          </a>
        </Link>
      </p>

      <p>
        <Link href='#comment-nous-utilisons-les-cookies'>
          <a data-smooth-scroll data-offset='80'>
            Comment utilisons-nous les cookies ?
          </a>
        </Link>
      </p>

      <p>
        <Link href='#vos-choix-cookies'>
          <a data-smooth-scroll data-offset='80'>
            Vos choix
          </a>
        </Link>
      </p>

      <p>
        <Link href='#modifications-apportées-à-cette-politique-cookies'>
          <a data-smooth-scroll data-offset='80'>
            Modifications apportées à cette Politique
          </a>
        </Link>
      </p>

      <p>
        <Link href='#nous-contacter-cookies'>
          <a data-smooth-scroll data-offset='80'>
            Nous contacter
          </a>
        </Link>
      </p>

      <h5 id='que-sont-les-cookies'>Que sont les cookies ?</h5>
      <p>
        Les cookies sont de petits fichiers de données stockés sur votre
        navigateur ou votre appareil, qui peuvent prendre la forme de cookies de
        session (qui expirent lorsque vous fermez votre navigateur Internet) ou
        de cookies persistants (qui restent sur votre navigateur pendant une
        période définie ou jusqu’à ce que vous les supprimiez). Ils contiennent
        généralement des informations telles que les préférences du site Web,
        les paramètres utilisateur et l’historique de navigation. Ces
        informations nous aident à vous reconnaître lorsque vous interagissez
        avec nos Services ou lorsque vous les consultez à nouveau.
      </p>
      <p>
        Les cookies peuvent être transmis par l’entité qui exploite le site Web
        que vous visitez (« cookies interne ») ou par d’autres sociétés («
        cookies tiers »). Par exemple, nous utilisons des cookies internes pour
        mémoriser vos préférences et paramètres, tels que la langue et
        l’emplacement. Nous intégrons également des cookies analytiques tiers,
        comme Google Analytics, sur nos sites Web pour nous aider à comprendre
        comment vous les utilisez, afin de les améliorer.
      </p>
      <p>
        Nous utilisons également d’autres technologies qui fonctionnent de la
        même manière que les cookies. Par exemple, nous utilisons des balises
        Internet (également appelées pixels), qui sont de petites images sur une
        page Internet ou dans un e-mail. Les balises Internet collectent des
        informations sur votre navigateur ou votre appareil, et peuvent définir
        des cookies. En outre, nous utilisons un stockage local, qui permet de
        stocker les données localement sur votre navigateur ou votre appareil.
      </p>

      <h5 id='comment-nous-utilisons-les-cookies'>
        Comment utilisons-nous les cookies ?
      </h5>
      <p>
        Nous utilisons des cookies pour un certain nombre de raisons, comme nous
        aider à déterminer la popularité de certains contenus, améliorer nos
        Services et votre expérience, ainsi que pour mieux comprendre votre
        activité en ligne. Les cookies que nous utilisons appartiennent
        généralement à une ou plusieurs des catégories suivantes :
      </p>
      <div className='eupp-container'>
        <table>
          <thead>
            <tr>
              <th>Catégories de cookies</th>
              <th>Comment utilisons-nous ces cookies ?</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Strictement nécessaires</td>
              <td>
                Ces cookies sont strictement nécessaires au bon fonctionnement
                de nos Services. Certains de nos Services dépendent de ces
                cookies et peuvent ne pas fonctionner correctement, ou ne pas
                fonctionner du tout, s’ils sont désactivés.
              </td>
            </tr>
            <tr>
              <td>Fonctionnels</td>
              <td>
                Ces cookies permettent au site Web de fournir des
                fonctionnalités et une personnalisation améliorées. Ils peuvent
                être définis par nous ou par des fournisseurs tiers dont nous
                avons ajouté les services à nos pages. Si vous n’autorisez pas
                ces cookies, certains ou tous ces services peuvent ne pas
                fonctionner correctement.
              </td>
            </tr>
            <tr>
              <td>De performance</td>
              <td>
                Nous utilisons ces cookies pour mieux comprendre comment vous
                interagissez avec nos Services afin de pouvoir les améliorer.
                <br />
                <br />
                Par exemple, nous pouvons utiliser ces cookies pour déterminer
                si vous avez interagi avec certains contenus ou certaines
                fonctionnalités. Nous pouvons également utiliser ces cookies
                pour en apprendre plus sur les fonctionnalités les plus
                populaires auprès de nos utilisateurs et les domaines où nous
                pourrions avoir besoin d’apporter des améliorations.
              </td>
            </tr>

            <tr>
              <td>De ciblage / Publicitaires</td>
              <td>
                Nos partenaires publicitaires de confiance et nous utilisons ces
                cookies pour afficher des publicités, rendre les publicités plus
                pertinentes pour les visiteurs de nos sites Web et suivre
                l’efficacité de toutes les campagnes publicitaires, à la fois
                concernant nos Services et d’autres sites Web.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h5 id='vos-choix-cookies'>Vos choix</h5>
      <p>
        Vous avez le droit de choisir d’accepter ou non les cookies. Cependant,
        étant donné que les cookies peuvent jouer un rôle important dans le
        fonctionnement de nos Services, si vous supprimez ou rejetez des
        cookies, cela pourrait affecter la disponibilité et la fonctionnalité de
        nos Services.
      </p>
      <p>
        Voici quelques options permettant de contrôler ou limiter la façon dont
        les cookies sont utilisés sur nos Services :
      </p>
      <ul>
        <li>
          <p>
            Vous pouvez généralement gérer les paramètres pour supprimer ou
            rejeter manuellement les cookies du navigateur dans les paramètres
            de configuration de votre navigateur. Pour gérer ces paramètres,
            veuillez suivre les instructions fournies par votre navigateur.
          </p>
        </li>
        <li>
          <p>
            Pour empêcher l’utilisation de vos données par Google Analytics,
            vous pouvez installer le{' '}
            <Link href='https://tools.google.com/dlpage/gaoptout'>
              <a>module complémentaire de navigateur de désactivation Google</a>
            </Link>
            .
          </p>
        </li>
        <li>
          <p>
            Pour obtenir des informations sur la façon dont nos partenaires
            publicitaires peuvent vous permettre de refuser de recevoir des
            annonces en fonction de votre historique de navigation sur Internet,
            veuillez consulter
            <Link href='http://optout.aboutads.info/'>
              <a> http://optout.aboutads.info/</a>
            </Link>
            . En outre, les utilisateurs européens doivent consulter l’{' '}
            <Link href='http://www.youronlinechoices.eu/'>
              <a>Association européenne pour la publicité numérique</a>
            </Link>{' '}
            et les utilisateurs canadiens l’{' '}
            <Link href='https://youradchoices.ca/'>
              <a>Alliance de publicité numérique du Canada</a>
            </Link>{' '}
            pour en apprendre plus sur la manière de refuser de recevoir ces
            types de publicité.
          </p>
        </li>
      </ul>

      <h5 id='modifications-apportées-à-cette-politique-cookies'>
        Modifications apportées à cette Politique
      </h5>
      <p>
        Nous pouvons de temps à autre mettre à jour ou modifier les cookies (ou
        technologies similaires) que nous utilisons. Nous mettrons en ligne la
        version la plus à jour de la présente Politique sur notre site Web à
        l’adresse{' '}
        <Link href='https://plaid.com/legal'>
          <a>https://plaid.com/legal</a>
        </Link>{' '}
        et, si nous réalisons des changements, nous mettrons à jour la date
        d’entrée en vigueur au début de cette Politique. Nous vous recommandons
        de vérifier régulièrement les éventuelles modifications.
      </p>

      <h5 id='nous-contacter-cookies'>Nous contacter</h5>
      <p>
        Si vous avez des questions concernant notre utilisation des cookies ou
        la présente Politique, vous pouvez nous contacter à l’adresse{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>
        .
      </p>

      <hr className='section-divider' />
    </>
  );
};

FrVersion.propTypes = propTypes;
export default FrVersion;
